import { InterceptedError } from '@/shared/api/intercepted-error';
import { useToggle } from '@mantine/hooks';
import { Fragment, ReactNode, useEffect } from 'react';
import {
  Anchor,
  Button,
  Collapse,
  Group,
  ScrollArea,
  Stack,
  Text,
  List,
  Badge,
  Code,
} from '@mantine/core';
import { parsePrimitiveFromString } from '@/shared/lib/utils/parse-primitive';
import { notifications } from '@mantine/notifications';
import IconArrowDown from '@tabler/icons-react/dist/esm/icons/IconArrowDown.mjs';
import cs from 'clsx';
import { useTranslation } from 'react-i18next';

export const DependenciesTemplate = (props: {
  error: InterceptedError;
  links: {
    href: string;
    label: string;
  }[];
}) => {
  const { t } = useTranslation(['translation']);
  if (!props.links || props.links.length === 0) return null;

  return (
    <Text data-testid='notification-dependencies-links'>
      {t('action.dependingElements')}
      {props.links?.map((el, i) => (
        <Fragment key={`${el.href}-${el.label}`}>
          {' '}
          <Anchor href={el.href} target='_blank'>
            {el.label}
          </Anchor>
          {props.links.length - 1 !== i && ','}
        </Fragment>
      ))}
    </Text>
  );
};

const DetailedAlert = ({
  error,
  id,
  renderErrorMetaData,
}: {
  error: InterceptedError;
  id: string;
  renderErrorMetaData?: (props: InterceptedError) => ReactNode;
}) => {
  const [opened, toggle] = useToggle();
  const { t } = useTranslation(['translation']);
  const data = error.response?.data as {
    title?: string;
    detail?: string;
    errors?: unknown[];
    invalid_params?: unknown[];
  } | null;
  const title = data?.title;
  const detail = data?.detail;
  const status = error.response?.status;
  const hasTitleOrDetail = Boolean(title ?? detail);
  const hasCollapseContent =
    (data?.errors?.length ?? 0) > 0 ||
    (data?.invalid_params?.length ?? 0) > 0 ||
    Boolean(renderErrorMetaData);

  useEffect(() => {
    if (opened) {
      notifications.update({ id, autoClose: false } as any);
    }
  }, [opened]);

  return (
    <Stack className='overflow-hidden' data-testid='notification-error-alert'>
      <Group justify='space-between' align='flex-start' wrap='nowrap' w='100%'>
        <Stack gap={4} style={{ flex: 1 }} className='min-w-0'>
          {hasTitleOrDetail ? (
            <>
              {title && (
                <Text fw={600} size='sm'>
                  {title}
                </Text>
              )}
              {detail && (
                <Text size='sm' c='dimmed'>
                  {detail}
                </Text>
              )}
            </>
          ) : (
            <Text fw={600} size='sm'>
              {error.message}
            </Text>
          )}
        </Stack>
        {status != null && (
          <Text size='xs' c='dimmed' ff='monospace' mt={2} className='shrink-0'>
            [{status}]
          </Text>
        )}
      </Group>

      {hasCollapseContent && (
        <>
          <Collapse
            expanded={opened}
            className={cs('!min-h-[30px] ', {
              '!block !opacity-100 mask-render-[linear-gradient(0deg,rgba(0,0,0,1)0%,rgba(0,0,0,0)0%,rgba(0,0,0,1)67%,rgba(0,0,0,1)70%)]':
                !opened,
            })}
          >
            <Stack
              className='overflow-hidden h-full pointer-events-auto'
              mah={180}
            >
              <ScrollArea.Autosize
                mah={180}
                scrollbars={opened ? 'y' : false}
                mt='xs'
                className='h-full'
              >
                <Stack>
                  {error.response?.data?.errors && (
                    <Stack>
                      <Text>ERRORS:</Text>
                      <List
                        classNames={{
                          itemWrapper: 'mr-[25px]',
                        }}
                      >
                        {error.response?.data?.errors
                          ?.toReversed()
                          .map((item, i) => (
                            <List.Item key={i}>
                              <Stack>
                                <Text>{item[0]}</Text>
                                <Text c='dimmed'>{item.slice(1)}</Text>
                              </Stack>
                            </List.Item>
                          ))}
                      </List>
                      {renderErrorMetaData && renderErrorMetaData(error)}
                    </Stack>
                  )}

                  {error.response?.data?.invalid_params && (
                    <Stack>
                      <Text>INVALID PARAMS:</Text>
                      <List
                        classNames={{
                          itemWrapper: 'mr-[25px]',
                        }}
                      >
                        {error.response?.data?.invalid_params?.map(
                          (item, i) => (
                            <List.Item key={i}>
                              <Stack>
                                <Group gap='xs'>
                                  <Text>{item.type}</Text>
                                  <Badge variant='light' color='red'>
                                    {parsePrimitiveFromString(item.input)}
                                  </Badge>
                                  <Text>at</Text>
                                  <Code> {item.loc?.join('.')}</Code>
                                </Group>
                                <Text c='dimmed'>{item.msg}</Text>
                              </Stack>
                            </List.Item>
                          ),
                        )}
                      </List>
                    </Stack>
                  )}
                </Stack>
              </ScrollArea.Autosize>
            </Stack>
          </Collapse>
          <Button
            variant='transparent'
            leftSection={
              <IconArrowDown
                size={16}
                style={{
                  transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            }
            color='red'
            data-testid='notification-error-details-btn'
            onClick={() => toggle()}
            className={'ml-6'}
          >
            {t('action.detail')}
          </Button>
        </>
      )}
    </Stack>
  );
};

export const requestError = (
  error: InterceptedError,
  renderErrorMetaData?: (error: InterceptedError) => ReactNode,
) => {
  const id = notifications.show({
    color: 'red',
    title: '',
    message: '',
  });

  notifications.update({
    id,
    color: 'red',
    classNames: {
      root: '!max-h-[400px]',
    },
    message: (
      <DetailedAlert
        error={error}
        id={id}
        renderErrorMetaData={renderErrorMetaData}
      />
    ),
  });
};